import axios, { AxiosInstance } from 'axios';
import React, { useMemo } from 'react';

type ServiceType = 'app' | 'detection';

type ClientMap = {
  [key in ServiceType]: AxiosInstance;
};

type Client = {
  clients: ClientMap;
  isReady: boolean;
};

const ClientContext = React.createContext<Client>(null as unknown as Client);

const ClientProvider: React.FC<
  React.PropsWithChildren<{
    url: string;
    detectionServiceUrl: string;
    token: string | undefined;
  }>
> = ({ url, detectionServiceUrl, token, children }) => {
  const clients = useMemo(() => {
    const appClient = axios.create({
      baseURL: url,
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    const detectionClient = axios.create({
      baseURL: detectionServiceUrl || url,
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return {
      app: appClient,
      detection: detectionClient,
    } as ClientMap;
  }, [url, detectionServiceUrl, token]);

  const contextValue = useMemo(() => {
    return { clients, isReady: !!token } as Client;
  }, [clients, token]);

  // todo why must this be a context provider?
  // we should just have it has a hook/module getter

  return (
    <ClientContext.Provider value={contextValue}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = (service: ServiceType = 'app') => {
  const { clients, isReady } = React.useContext(ClientContext);
  return { client: clients[service], isReady };
};

export default ClientProvider;
